/**
 * The non-serializable half of the editor (plan D18).
 *
 * `EditorState` is plain data; the imported model, the bound rig, the built skeleton and the
 * source GLB bytes are live objects that must never enter the store. They live here, in a plain
 * holder that any module can read without subscribing to state changes.
 *
 * The two live slots use the real wave-2 types through `import type` only, so nothing from
 * `src/importExport` or `src/runtime` is pulled into the bundle by the state layer.
 */

import type { Object3D, Skeleton } from 'three';
import type { ImportedModel } from '../importExport';
import type { BoundRig } from '../runtime';
import {
  collectSceneResources,
  disposeSceneResources,
  sharesResource,
} from './viewport/disposeResources';
import type { SceneResources } from './viewport/disposeResources';

/** The model currently loaded in the editor (`ImportedModel`, src/importExport/glbImport.ts). */
export type SessionModel = ImportedModel;

/** The rig driving the loaded skeleton (`BoundRig`, src/runtime/boundRig.ts, plan D18). */
export type SessionRig = BoundRig;

/**
 * A saved project loaded READ-ONLY beside the open one, to preview the same pose on another
 * character ("companion", `previewController`).
 *
 * Deliberately thin: a companion is a scene root plus something that can be posed. It is never
 * edited, never exported and never persisted, so none of the open project's machinery (mapping
 * draft, placement, skin params, undo) applies to it. `root` is NOT under `model.root` — for an
 * assembled unrigged companion it is the group `assembleSkinnedRig` returned — so whoever poses
 * one has to refresh its world matrices itself.
 */
export interface SessionCompanion {
  projectId: string;
  root: Object3D;
  rig: BoundRig;
}

export interface EditorSession {
  model: SessionModel | null;
  rig: SessionRig | null;
  /** Skeleton built by RIG mode (unrigged workflow), before/after skinning. */
  builtSkeleton?: Skeleton | null;
  sourceGlb: ArrayBuffer | null;
  /** Read-only preview characters shown beside the open model, in the order they were added. */
  companions: SessionCompanion[];

  setModel(model: SessionModel | null): void;
  setRig(rig: SessionRig | null): void;
  setBuiltSkeleton(skeleton: Skeleton | null): void;
  setSourceGlb(buffer: ArrayBuffer | null): void;
  /** Append a companion. A second one for the same project id replaces the first. */
  addCompanion(companion: SessionCompanion): void;
  /** Drop one companion and return it, so the caller can dispose what it owns. Null if absent. */
  removeCompanion(projectId: string): SessionCompanion | null;
  /** Drop every companion and return them, in order, for disposal. */
  clearCompanions(): SessionCompanion[];
  /** Drop every live object (project closed / new import). */
  clear(): void;
}

export function createSession(): EditorSession {
  /**
   * The GPU resources of the IMPORT that is currently loaded (audit §2.6 #1).
   *
   * The same owned-map pattern the companion path uses (`previewController.ts`, vault
   * `viewport-companions-onion`) and for the same reason: the live objects live somewhere else,
   * and the transitions that are supposed to release them are exactly the ones that drop the last
   * reference to them. Until 2026-08-22 nothing freed an imported model at all — every project
   * swap kept the whole mesh resident for the life of the tab, because `WebGLBindingStates`
   * releases `bindingStates[geometry.id].attributes` only on the geometry's own `dispose()`.
   *
   * `setModel` and `clear()` are the two choke points where a model stops being current, so this
   * is where the release belongs. `projectController` reaches both (`loadIntoSession` on a new
   * import, `closeProject` on a close) without needing to know what a GPU resource is.
   */
  let ownedModelResources: SceneResources | null = null;

  function releaseModelResources(): void {
    if (!ownedModelResources) return;
    disposeSceneResources(ownedModelResources);
    ownedModelResources = null;
  }

  const session: EditorSession = {
    model: null,
    rig: null,
    builtSkeleton: null,
    sourceGlb: null,
    companions: [],

    setModel(model): void {
      /*
       * Not every `setModel` is a replacement. `rigController.swapModel` calls this on every
       * "Generate skin weights" with a DERIVATIVE of the model already loaded: a new root holding
       * skinned clones, while the plain source meshes stay alive off-scene in the rig
       * controller's `sourceMeshes` for the next generation, and the clones share their material
       * objects with those source meshes (`src/rig/skeletonBuilder.ts`, `createSkinnedMesh` hands
       * `mesh.material` through without cloning). Freeing the outgoing subtree there would rip
       * resources out from under objects that are still in use.
       *
       * The discriminator is that sharing itself: a model that has ANY resource object in common
       * with the import we own is a derivative of that import, not a replacement for it. A real
       * replacement comes out of its own `importGlbFromArrayBuffer` call, and GLTFLoader
       * allocates fresh geometries, materials and textures per parse, so the overlap is empty.
       * Deliberately NOT keyed on `sourceGlb`, which would make this depend on the order of two
       * setter calls in a file this module does not own.
       *
       * A derivative records nothing: the only resources it adds are the cloned geometries and
       * the built `Skeleton` from `assembleSkinnedRig`, and `rigController` already owns and
       * releases exactly those (its `OwnedRigResources`). Recording them here would dispose them
       * twice and blur which layer is responsible.
       */
      const incoming = model ? collectSceneResources(model.root) : null;
      if (incoming && ownedModelResources && sharesResource(incoming, ownedModelResources)) {
        session.model = model;
        return;
      }
      /*
       * Safe to free before the swap is announced because every caller is synchronous: nothing
       * renders between this line and the `'model-changed'` emit that follows it, so the viewport
       * never draws a geometry after it was disposed. Same ordering argument as
       * `rigController.assemble`.
       */
      releaseModelResources();
      ownedModelResources = incoming;
      session.model = model;
    },
    setRig(rig): void {
      session.rig = rig;
    },
    setBuiltSkeleton(skeleton): void {
      session.builtSkeleton = skeleton;
    },
    setSourceGlb(buffer): void {
      session.sourceGlb = buffer;
    },
    addCompanion(companion): void {
      const existing = session.companions.findIndex((c) => c.projectId === companion.projectId);
      if (existing >= 0) session.companions[existing] = companion;
      else session.companions.push(companion);
    },
    removeCompanion(projectId): SessionCompanion | null {
      const index = session.companions.findIndex((c) => c.projectId === projectId);
      if (index < 0) return null;
      return session.companions.splice(index, 1)[0] ?? null;
    },
    clearCompanions(): SessionCompanion[] {
      return session.companions.splice(0);
    },
    clear(): void {
      // The project is closed: the import is finished and nothing outside it can still be using
      // its geometries, materials or textures. `rigController.resetInternal()` has already freed
      // what IT allocated (the skinned clones, the built skeleton) by the time `closeProject`
      // gets here, and the two owned sets are disjoint by construction — see `setModel`.
      releaseModelResources();
      session.model = null;
      session.rig = null;
      session.builtSkeleton = null;
      session.sourceGlb = null;
      // Companions belong to whatever project was open; nothing keeps them alive past it. The
      // caller that owns their GPU resources (`previewController`) disposes them separately —
      // `clear()` only guarantees the session no longer points at them.
      session.companions.length = 0;
    },
  };
  return session;
}
